import { Control, Controller, UseFormRegister, FieldErrors, useWatch } from "react-hook-form";
import styles from "./UtilityProfileForm.module.scss";
import CommonFormFieldWrapper, {
	getFormInputErrors,
} from "../../../../../common/components/form-field-wrapper/CommonFormFieldWrapper";
import { Select } from "../../../../customer/components";
import { UtilityColorField } from "./UtilityColorField";
import { UtilityLogoUpload } from "./UtilityLogoUpload";
import { UTILITY_LANGUAGES, UtilityConfigurationLookup } from "../../../../../model/bps-admin/utility-configuration";

export interface UtilityProfileFormValues {
	name: string;
	currencyId: string;
	acronym: string;
	subDomain: string;
	/** A File once picked; on edit it starts out as the URL of the logo already stored. */
	logo: File | string | null;
	primaryColor: string;
	secondaryColor: string;
	accentColor: string;
	countryId: string;
	language: string;
}

interface UtilityProfileFormProps {
	formId: string;
	control: Control<UtilityProfileFormValues>;
	register: UseFormRegister<UtilityProfileFormValues>;
	errors: FieldErrors<UtilityProfileFormValues>;
	onSubmit: () => void;
	countries: UtilityConfigurationLookup[];
	currencies: UtilityConfigurationLookup[];
	/** Fixed at creation: the DNS record and the gateway listener are keyed by it. */
	isSubDomainLocked: boolean;
}

const toOptions = (lookups: UtilityConfigurationLookup[]) =>
	lookups.map(lookup => ({ value: lookup.id, label: lookup.name }));

// The backend rejects anything over 1 MB, measured as bytes / 1024 / 1024.
const MAX_LOGO_SIZE_BYTES = 1024 * 1024;

// Same list as the backend's ImageFileValidator. Checked by extension rather than
// File.type, which comes back empty for some drag-and-drop sources.
const ALLOWED_LOGO_EXTENSIONS = ["png", "jpg", "jpeg", "svg"];

// The zone new records are created in, taken from the host the admin portal is served on -
// xepp-mt-staging.bpsafrica.com and xepp-mt-prod.bpsafrica.com both yield bpsafrica.com, which
// is what the backend has as GoDaddy:Domain. Empty on localhost, where the preview is hidden
// rather than guessed at.
const dnsZone = (() => {
	const labels = window.location.hostname.split(".");

	return labels.length > 2 ? labels.slice(-2).join(".") : "";
})();

// Mirrors NormaliseHost in TenantDomainProvisioningService: a value that already carries the
// zone is used as-is, anything else gets it appended. Shown rather than hidden so that a full
// host from another zone reads as portal.acme.com.bpsafrica.com here, which is exactly what
// would be created.
const toFullHost = (subDomain: string) => {
	const value = subDomain.trim().replace(/^\.+|\.+$/g, "").toLowerCase();

	if (!value || !dnsZone) return "";

	return value.endsWith(`.${dnsZone}`) ? value : `${value}.${dnsZone}`;
};

const hasAllowedLogoExtension = (file: File) =>
	ALLOWED_LOGO_EXTENSIONS.includes(file.name.split(".").pop()?.toLowerCase() ?? "");

export function UtilityProfileForm({
	formId,
	control,
	register,
	errors,
	onSubmit,
	countries,
	currencies,
	isSubDomainLocked,
}: Readonly<UtilityProfileFormProps>) {
	const subDomain = useWatch({ control, name: "subDomain" });

	return (
		<form id={formId} className={styles.form} onSubmit={onSubmit}>
			<CommonFormFieldWrapper title="Name" errors={getFormInputErrors(errors.name?.message)}>
				<input
					type="text"
					className={styles.textInput}
					placeholder="Utility Name"
					{...register("name", { required: "Name is required" })}
				/>
			</CommonFormFieldWrapper>

			<Controller
				name="currencyId"
				control={control}
				rules={{ required: "Currency is required" }}
				render={({ field }) => (
					<Select
						className={styles.select}
						label="Currency"
						placeholder="Select currency"
						options={toOptions(currencies)}
						value={field.value}
						onChange={field.onChange}
						error={errors.currencyId?.message}
					/>
				)}
			/>

			<CommonFormFieldWrapper title="Acronym" errors={getFormInputErrors(errors.acronym?.message)}>
				<input
					type="text"
					className={styles.textInput}
					placeholder="Utility Acronym"
					{...register("acronym", { required: "Acronym is required" })}
				/>
			</CommonFormFieldWrapper>

			<CommonFormFieldWrapper
				title="Subdomain"
				errors={getFormInputErrors(errors.subDomain?.message)}
				suffix={toFullHost(subDomain)}
			>
				<input
					type="text"
					className={styles.textInput}
					placeholder="Utility Subdomain"
					readOnly={isSubDomainLocked}
					{...register("subDomain", { required: "Subdomain is required" })}
				/>
			</CommonFormFieldWrapper>

			<Controller
				name="logo"
				control={control}
				rules={{
					required: "Logo is required",
					// Only a newly picked file is validated; an already stored one is just a URL.
					validate: {
						type: file =>
							!(file instanceof File) || hasAllowedLogoExtension(file) || "Logo must be a PNG, JPG or SVG file",
						size: file =>
							!(file instanceof File) || file.size <= MAX_LOGO_SIZE_BYTES || "Logo must not be larger than 1 MB",
					},
				}}
				render={({ field }) => (
					<CommonFormFieldWrapper title="Utility Logo" borderNotVisible childrenColumnDirection>
						<UtilityLogoUpload value={field.value} onChange={field.onChange} error={errors.logo?.message} />
					</CommonFormFieldWrapper>
				)}
			/>

			<div className={styles.colorRow}>
				{(
					[
						["primaryColor", "Primary Color"],
						["secondaryColor", "Secondary"],
						["accentColor", "Accent"],
					] as const
				).map(([name, label]) => (
					<Controller
						key={name}
						name={name}
						control={control}
						rules={{
							required: `${label} is required`,
							pattern: { value: /^#[0-9a-fA-F]{6}$/, message: "Use a 6-digit hex value" },
						}}
						render={({ field }) => (
							<UtilityColorField
								label={label}
								value={field.value}
								onChange={field.onChange}
								error={errors[name]?.message}
							/>
						)}
					/>
				))}
			</div>

			<Controller
				name="countryId"
				control={control}
				rules={{ required: "Country is required" }}
				render={({ field }) => (
					<Select
						className={styles.select}
						label="Country"
						placeholder="Select country"
						options={toOptions(countries)}
						value={field.value}
						onChange={field.onChange}
						error={errors.countryId?.message}
					/>
				)}
			/>

			<Controller
				name="language"
				control={control}
				rules={{ required: "Language is required" }}
				render={({ field }) => (
					<Select
						className={styles.select}
						label="Language"
						placeholder="Select language"
						options={UTILITY_LANGUAGES}
						value={field.value}
						onChange={field.onChange}
						error={errors.language?.message}
					/>
				)}
			/>
		</form>
	);
}
